import {bytesToSize, formatMinutes, secondsToUnits} from "@/utils/textFormatting";
import {Tooltip} from "flowbite-react";
import TrashIcon from "@/Components/Icons/TrashIcon";
import ProgressBar from "@/Components/ProgressBar";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {LocalUpload, RemoteUpload} from "@/Pages/Dashboard/Upload/UploadModal";
import {useMemo} from "react";

type UploadItemProps = {
    upload: LocalUpload | RemoteUpload;
    showAdvancedSettings: () => void;
    cancelUpload: () => void;
    duration?: number;
    uploadEta?: number;
};

export default function UploadItem({
    upload,
    duration,
    uploadEta,
    cancelUpload,
    showAdvancedSettings
}: UploadItemProps) {
    const isExtracting = "file" in upload && upload.step === "extracting";
    const hasCustomDuration = useMemo(() => {
        return upload.options.timeEnd !== undefined;
    }, [upload.options]);

    return (
        <div className="group">
            <div className="flex mb-2 items-center gap-x-3">
        <span
            className="size-8 flex flex-shrink-0 justify-center items-center border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 rounded-lg">
            {
                upload.isAudioOnly ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                             stroke="currentColor" className="size-5">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"/>
                        </svg>)
                    :

                    (<svg
                            className="size-5"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" x2="12" y1="3" y2="15"/>
                        </svg>
                    )

            }



        </span>
                <div className="flex flex-col grow overflow-hidden ">
                    <p className="text-sm text-ellipsis truncate notranslate font-medium text-gray-800 dark:text-gray-200">
                        {"file" in upload ? upload.file.name : upload.fileName}
                    </p>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {isExtracting ? (
                            <span>Extracting audio...</span>
                        ) : (
                            <span>
                              {bytesToSize("file" in upload ? upload.file.size : upload.fileSize)}
                            </span>
                        )}
                        {upload.progress < 1 && uploadEta !== undefined && (() => {
                            const {value, unit} = secondsToUnits(uploadEta);
                            return (
                                <span>
                                    <span className="mx-1">|</span>
                                    <span className="notranslate">{value}</span><span> {unit} left</span>
                                </span>
                            );
                        })()}
                        {!!duration && (
                            <span>
                                <span className="mx-1">|</span>
                                <span className={hasCustomDuration ? 'text-blue-500 dark:text-blue-400 font-bold' : ''}>{formatMinutes(duration / 60)} long</span>
                            </span>
                        )}
                    </p>

                </div>
                <div className="flex items-center">

                    <Tooltip
                        content={"Advanced Settings"}
                        trigger="hover"
                        placement="top"
                    >
              <span onClick={showAdvancedSettings}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className={`w-5 h-5 text-gray-300 dark:text-gray-600 mr-0.5   group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-all hover:!text-gray-800 dark:hover:!text-gray-200 cursor-pointer ${
                        Object.keys(upload.options || {}).length
                            ? "!text-blue-500 dark:!text-blue-400 !opacity-100"
                            : ""
                    }`}
                >
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                  />
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </span>
                    </Tooltip>

                    <Tooltip content={"Remove"} trigger="hover" placement="top">
            <span
                className="text-gray-300 dark:text-gray-600 cursor-pointer transition-all group-hover:text-gray-400 dark:group-hover:text-gray-500 hover:!text-gray-800 dark:hover:!text-gray-200"
                onClick={cancelUpload}
            >
              <TrashIcon className="size-4"/>
            </span>
                    </Tooltip>
                </div>
            </div>

            <div className="flex flex-row items-center">
                <ProgressBar
                    progress={upload.progress * 100}
                    height="sm"
                    color={
                        upload.error
                            ? "red"
                            : "file" in upload && upload.step === "extracting"
                                ? "orange"
                                : "blue"
                    }
                />
                <span className="text-xs ml-1 w-8 text-right dark:text-white">
          <span className="notranslate ">{Math.round(upload.progress * 100)}</span><span>%</span>
        </span>
            </div>
            {upload.error && (
                <div className="flex items-center mt-2 text-red-500 dark:text-red-400">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2"/>
                    <span className="text-sm">{upload.error}</span>
                </div>
            )}
        </div>
    );
}
